/**
 * Structured Data (JSON-LD) utilities for SEO
 * Helps search engines understand the content and context of the website
 */

export interface OrganizationData {
  name: string;
  url: string;
  logo?: string;
  description?: string;
  contactPoint?: {
    telephone?: string;
    contactType?: string;
    email?: string;
    areaServed?: string | string[];
  };
  sameAs?: string[];
  address?: {
    addressCountry: string;
    addressLocality: string;
    addressRegion?: string;
    postalCode?: string;
    streetAddress?: string;
  };
  areaServed?: string | string[];
  aggregateRating?: {
    ratingValue: number;
    reviewCount: number;
    bestRating?: number;
    worstRating?: number;
  };
  reviews?: Array<{
    author: string;
    reviewBody: string;
    reviewRating?: { ratingValue: number; bestRating?: number; worstRating?: number };
  }>;
}

export interface ServiceData {
  name: string;
  description: string;
  provider: {
    name: string;
    url: string;
  };
  areaServed?: string | string[];
  serviceType?: string;
}

export interface BreadcrumbData {
  items: Array<{
    name: string;
    url: string;
  }>;
}

export interface FAQData {
  questions: Array<{
    question: string;
    answer: string;
  }>;
}

export interface ReviewData {
  reviews: Array<{
    author: string;
    datePublished?: string;
    reviewBody: string;
    reviewRating?: {
      ratingValue: number;
      bestRating?: number;
      worstRating?: number;
    };
  }>;
  aggregateRating?: {
    ratingValue: number;
    reviewCount: number;
    bestRating?: number;
    worstRating?: number;
  };
}

export interface ArticleData {
  headline: string;
  description: string;
  image?: string | string[];
  datePublished: string;
  dateModified?: string;
  author: {
    name: string;
    url?: string;
  };
  publisher: {
    name: string;
    logo?: string;
  };
  articleSection?: string;
  keywords?: string[];
}

/**
 * Generate Organization structured data
 */
export const generateOrganizationSchema = (data: OrganizationData) => {
  // Handle areaServed - can be string or array
  const getAreaServed = () => {
    const areaServed = data.areaServed || data.contactPoint?.areaServed;
    if (!areaServed) return undefined;
    
    if (Array.isArray(areaServed)) {
      return areaServed.map(country => ({
        "@type": "Country",
        name: country,
      }));
    }
    return {
      "@type": "Country",
      name: areaServed,
    };
  };

  const areaServedValue = getAreaServed();
  const contactPoint = data.contactPoint ? {
    "@type": "ContactPoint",
    ...data.contactPoint,
    ...(data.contactPoint.areaServed && {
      areaServed: Array.isArray(data.contactPoint.areaServed)
        ? data.contactPoint.areaServed.map(country => ({
            "@type": "Country",
            name: country,
          }))
        : {
            "@type": "Country",
            name: data.contactPoint.areaServed,
          },
    }),
  } : undefined;

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: data.name,
    url: data.url,
    ...(data.logo && {
      logo: data.logo,
    }),
    ...(data.description && {
      description: data.description,
    }),
    ...(contactPoint && {
      contactPoint,
    }),
    ...(areaServedValue && {
      areaServed: areaServedValue,
    }),
    ...(data.sameAs && data.sameAs.length > 0 && {
      sameAs: data.sameAs,
    }),
    ...(data.address && {
      address: {
        "@type": "PostalAddress",
        ...data.address,
      },
    }),
    ...(data.aggregateRating && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: data.aggregateRating.ratingValue,
        reviewCount: data.aggregateRating.reviewCount,
        ...(data.aggregateRating.bestRating !== undefined && {
          bestRating: data.aggregateRating.bestRating,
        }),
        ...(data.aggregateRating.worstRating !== undefined && {
          worstRating: data.aggregateRating.worstRating,
        }),
      },
    }),
    ...(data.reviews && data.reviews.length > 0 && {
      review: data.reviews.map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.author },
        reviewBody: r.reviewBody,
        ...(r.reviewRating && {
          reviewRating: {
            "@type": "Rating",
            ratingValue: r.reviewRating.ratingValue,
            ...(r.reviewRating.bestRating !== undefined && {
              bestRating: r.reviewRating.bestRating,
            }),
            ...(r.reviewRating.worstRating !== undefined && {
              worstRating: r.reviewRating.worstRating,
            }),
          },
        }),
      })),
    }),
  };
};

/**
 * Generate Service structured data
 */
export const generateServiceSchema = (data: ServiceData) => {
  // Handle areaServed - can be string or array
  const getAreaServed = () => {
    if (!data.areaServed) return undefined;
    
    if (Array.isArray(data.areaServed)) {
      return data.areaServed.map(country => ({
        "@type": "Country",
        name: country,
      }));
    }
    return {
      "@type": "Country",
      name: data.areaServed,
    };
  };

  const areaServedValue = getAreaServed();

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: data.name,
    description: data.description,
    provider: {
      "@type": "Organization",
      name: data.provider.name,
      url: data.provider.url,
    },
    ...(areaServedValue && {
      areaServed: areaServedValue,
    }),
    ...(data.serviceType && {
      serviceType: data.serviceType,
    }),
  };
};

/**
 * Generate LocalBusiness structured data
 */
export const generateLocalBusinessSchema = (data: OrganizationData) => {
  // Handle areaServed for LocalBusiness
  const getAreaServed = () => {
    const areaServed = data.areaServed || data.contactPoint?.areaServed;
    if (!areaServed) return undefined;
    
    if (Array.isArray(areaServed)) {
      return areaServed.map(country => ({
        "@type": "Country",
        name: country,
      }));
    }
    return {
      "@type": "Country",
      name: areaServed,
    };
  };

  const areaServedValue = getAreaServed();

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: data.name,
    url: data.url,
    ...(data.logo && {
      image: data.logo,
    }),
    ...(data.description && {
      description: data.description,
    }),
    ...(data.address && {
      address: {
        "@type": "PostalAddress",
        ...data.address,
      },
    }),
    ...(data.contactPoint && {
      telephone: data.contactPoint.telephone,
      email: data.contactPoint.email,
    }),
    ...(areaServedValue && {
      areaServed: areaServedValue,
    }),
  };
};

/**
 * Generate BreadcrumbList structured data
 */
export const generateBreadcrumbSchema = (data: BreadcrumbData) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: data.items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

export const generateWebSiteSchema = (
  siteUrl: string,
  siteName: string,
  description?: string
) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteName,
    url: siteUrl,
    ...(description && { description }),
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${siteUrl}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
};

/**
 * Generate FAQPage structured data
 */
export const generateFAQSchema = (data: FAQData) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.questions.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
};

/**
 * Generate AggregateRating and Review structured data
 */
export const generateReviewSchema = (data: ReviewData) => {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Organization",
  };

  if (data.aggregateRating) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: data.aggregateRating.ratingValue,
      reviewCount: data.aggregateRating.reviewCount,
      ...(data.aggregateRating.bestRating && {
        bestRating: data.aggregateRating.bestRating,
      }),
      ...(data.aggregateRating.worstRating && {
        worstRating: data.aggregateRating.worstRating,
      }),
    };
  }

  if (data.reviews && data.reviews.length > 0) {
    schema.review = data.reviews.map((review) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: review.author,
      },
      ...(review.datePublished && {
        datePublished: review.datePublished,
      }),
      reviewBody: review.reviewBody,
      ...(review.reviewRating && {
        reviewRating: {
          "@type": "Rating",
          ratingValue: review.reviewRating.ratingValue,
          ...(review.reviewRating.bestRating && {
            bestRating: review.reviewRating.bestRating,
          }),
          ...(review.reviewRating.worstRating && {
            worstRating: review.reviewRating.worstRating,
          }),
        },
      }),
    }));
  }

  return schema;
};

/**
 * Generate Article structured data
 */
export const generateArticleSchema = (data: ArticleData) => {
  const imageArray = Array.isArray(data.image) ? data.image : data.image ? [data.image] : undefined;

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: data.headline,
    description: data.description,
    ...(imageArray && {
      image: imageArray,
    }),
    datePublished: data.datePublished,
    ...(data.dateModified && {
      dateModified: data.dateModified,
    }),
    author: {
      "@type": "Person",
      name: data.author.name,
      ...(data.author.url && {
        url: data.author.url,
      }),
    },
    publisher: {
      "@type": "Organization",
      name: data.publisher.name,
      ...(data.publisher.logo && {
        logo: {
          "@type": "ImageObject",
          url: data.publisher.logo,
        },
      }),
    },
    ...(data.articleSection && {
      articleSection: data.articleSection,
    }),
    ...(data.keywords && data.keywords.length > 0 && {
      keywords: data.keywords.join(", "),
    }),
  };
};
